import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface RequestVerificationParams {
  email: string;
  type: "registration" | "password_reset";
  cdkCode?: string;
}

export function useEmailVerification() {
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const publishableKey =
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;
  const publicInvokeHeaders = publishableKey
    ? {
        apikey: publishableKey,
        Authorization: `Bearer ${publishableKey}`,
      }
    : undefined;

  const requestVerification = async ({ email, type, cdkCode }: RequestVerificationParams) => {
    setIsLoading(true);
    try {
      // 启动阿里云图形验证码 (Dynamic import)
      const { runAlicomCaptcha } = await import("@/lib/aliyun-captcha");
      const captchaRes = await runAlicomCaptcha();

      const { data, error } = await supabase.functions.invoke("request-verification", {
        body: { 
          email, 
          type, 
          cdkCode,
          ...captchaRes 
        },
        headers: publicInvokeHeaders,
      });

      // Edge function returns error in data.error even on non-2xx
      if (error) {
        // Try to extract the actual error message from the response
        const msg = data?.error || error.message;
        // Replace generic messages with user-friendly ones
        if (msg.includes("non-2xx") || msg.includes("FunctionsHttpError")) {
          throw new Error(data?.error || "服务请求失败，请检查网络连接后重试");
        }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);

      // 开始60秒倒计时
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return { success: true };
    } catch (error: any) {
      const message = error.message || "未知错误，请稍后再试";
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async (email: string, code: string, type: "registration" | "password_reset") => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("verify-code", {
        body: { email, code, type },
        headers: publicInvokeHeaders,
      });

      if (error) {
        const msg = data?.error || error.message;
        if (msg.includes("non-2xx") || msg.includes("FunctionsHttpError")) {
          throw new Error(data?.error || "验证服务异常，请稍后再试");
        }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);

      return { success: true, verified: data.verified };
    } catch (error: any) {
      const message = error.message || "验证失败，请稍后再试";
      return { success: false, verified: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    countdown,
    requestVerification,
    verifyCode,
  };
}
